/**
 * Cart upsell variant picker.
 *
 * <cart-upsell-variants> wraps one <select> per product option inside an
 * upsell card's product form (snippets/cart-upsell-card.liquid) and carries
 * a JSON script of the product's variants. The element starts [hidden] with
 * "Choose <option>" placeholders; clicking add-to-cart swaps the CTA for the
 * selects, and completing the pick resolves the variant, updates the card's
 * price/image and hidden [name=id], swaps the CTA back in and submits — so
 * choosing a variant IS the add. Dead ends (sold out / nonexistent combo)
 * keep the picker up with a disabled CTA below it as the message.
 *
 * A custom element rather than a delegated listener because the drawer is
 * replaced wholesale on every cart change (?sections=cart-drawer): new
 * instances upgrade themselves on insertion, so nothing needs re-binding.
 */
if (!customElements.get('cart-upsell-variants')) {
  customElements.define(
    'cart-upsell-variants',
    class CartUpsellVariants extends HTMLElement {
      connectedCallback() {
        const json = this.querySelector('script[type="application/json"]');
        try {
          this.variants = json ? JSON.parse(json.textContent) : [];
        } catch (e) {
          this.variants = [];
        }
        this.selects = Array.from(this.querySelectorAll('select[data-option-index]'));
        this.card = this.closest('.cart-upsell-card');
        this.form = this.closest('form');
        if (!this.card || !this.form || !this.variants.length) return;

        this.onChange = this.onChange.bind(this);
        this.selects.forEach((s) => s.addEventListener('change', this.onChange));

        // Deferred pick: the element starts [hidden] with placeholder options.
        // The first add-to-cart click swaps the CTA for the picker; completing
        // the pick swaps back and submits, so choosing a variant IS the add.
        this.button = this.form.querySelector('[type="submit"]');
        if (this.hidden && this.button) {
          this.onRevealClick = this.onRevealClick.bind(this);
          this.button.addEventListener('click', this.onRevealClick);
        }
      }

      disconnectedCallback() {
        if (this.selects && this.onChange) {
          this.selects.forEach((s) => s.removeEventListener('change', this.onChange));
        }
        if (this.button && this.onRevealClick) {
          this.button.removeEventListener('click', this.onRevealClick);
        }
      }

      onRevealClick(event) {
        if (!this.hidden) return;
        // A complete, available selection (e.g. a retry after a failed add)
        // submits normally instead of reopening the picker.
        if (!this.selects.some((s) => !s.value)) {
          const variant = this.findVariant(this.selectedOptions());
          if (variant && variant.available) return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.hidden = false;
        this.button.style.display = 'none';
        const idInput = this.form.querySelector('[name="id"]');
        if (idInput) idInput.disabled = true;
        if (this.selects[0]) this.selects[0].focus();
      }

      selectedOptions() {
        const values = [];
        this.selects.forEach((s) => {
          values[Number(s.dataset.optionIndex)] = s.value;
        });
        return values;
      }

      findVariant(values) {
        return this.variants.find((v) => v.options.every((opt, i) => opt === values[i])) || null;
      }

      onChange(event) {
        // cart-drawer-items listens for `change` on itself and treats every
        // target as a quantity input (parseInt -> NaN -> step error ->
        // validity bubble + crash), so keep the select's event local.
        if (event) event.stopPropagation();
        const idInput = this.form.querySelector('[name="id"]');
        const button = this.form.querySelector('[type="submit"]');
        const buttonText = button && button.querySelector('span');

        if (this.selects.some((s) => !s.value)) {
          // Options still on their placeholder — keep the picker up.
          if (idInput) idInput.disabled = true;
          return;
        }

        const variant = this.findVariant(this.selectedOptions());
        const price = this.card.querySelector('.cart-upsell-card__final-price');
        const oldPrice = this.card.querySelector('.cart-upsell-card__old-price');
        const image = this.card.querySelector('.cart-upsell-card__image');

        if (!variant) {
          // Option combination that does not exist as a variant: keep the
          // picker up, with a disabled CTA below it as the explanation.
          if (idInput) idInput.disabled = true;
          if (button) {
            button.style.display = '';
            button.disabled = true;
          }
          if (buttonText) buttonText.textContent = this.dataset.unavailableText || 'Unavailable';
          return;
        }

        // Prices are Liquid `money` output (may include <span class=money>), so HTML.
        if (price) price.innerHTML = variant.price;
        if (oldPrice) {
          if (variant.compare_at_price) {
            oldPrice.innerHTML = variant.compare_at_price;
            oldPrice.hidden = false;
          } else {
            oldPrice.innerHTML = '';
            oldPrice.hidden = true;
          }
        }
        if (image && variant.image) {
          image.src = variant.image;
          image.removeAttribute('srcset');
        }

        if (!variant.available) {
          // Keep the picker up so another variant can be chosen; the disabled
          // CTA below it carries the message.
          if (idInput) idInput.disabled = true;
          if (button) {
            button.style.display = '';
            button.disabled = true;
          }
          if (buttonText) buttonText.textContent = this.dataset.soldOutText || 'Sold out';
          return;
        }

        if (idInput) {
          idInput.value = variant.id;
          idInput.disabled = false;
        }
        // The pick IS the add: swap the CTA back in (product-form runs its
        // loading spinner on it) and submit.
        this.hidden = true;
        if (button) {
          button.style.display = '';
          button.disabled = false;
          button.removeAttribute('aria-disabled');
          if (buttonText) buttonText.textContent = this.dataset.addText || 'Add to cart';
        }
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
        else this.form.submit();
      }
    }
  );
}
